<template>
  <div class="candidates">
    <div class="step1" v-if="votingEnabled && !voted && step === 1">
      <v-row>
        <div class="max-choices" v-if="maxChoices > 0">
          <v-col>
            Максимален брой избори: {{ maxChoices }} (Избрани: {{ choiceCount }})
          </v-col>
        </div>
      </v-row>
      <v-row>
        <v-col cols="12">
          <table>
            <tr class="thead">
              <td></td>
              <td>Кандидат</td>
              <td>Място</td>
            </tr>

            <tr v-for="candidate in candidates" :key="candidate.id" :class="{ selected: candidate.place > 0 }">
              <td>{{ candidate.id }}.</td>
              <td v-html="candidate.name"></td>
              <td>
                <v-select :items="places" label="---" v-model="candidate.place" solo clearable @change="makeSelection"
                  :disabled="maxChoices ? candidateDisabled(candidate) : false"></v-select>
              </td>
            </tr>
          </table>
        </v-col>
      </v-row>

      <p v-if="candidates.length > 0 && selected.length < minSelections">
        Трябва да направите поне {{ minSelections }} {{ minSelections > 1 ? 'избора' : 'избор' }}.
      </p>

      <v-row>
        <v-col cols="12">
          <v-btn @click="nextStep" :disabled="selected.length < minSelections" color="primary">
            Напред <v-icon class="ml-3">send</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <div class="step2" v-if="err.length === 0 && !voted && step === 2">
      <v-row>
        <v-col cols="12">
          <h3 class="title mb-5">Моля потвърдете Вашия избор.</h3>

          <table>
            <tr class="thead">
              <td></td>
              <td>Кандидат</td>
              <td>Място</td>
            </tr>

            <tr v-for="candidate in candidates" :key="candidate.id" :class="{ selected: candidate.place > 0 }">
              <td>{{ candidate.id }}.</td>
              <td v-html="candidate.name"></td>
              <td v-html="candidate.place > 0 ? candidate.place : '---'"></td>
            </tr>
          </table>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-checkbox name="age-declaration" v-model="ageDeclared" label="Декларирам, че имам навършени 18 години."
            color="primary" hide-details></v-checkbox>
        </v-col>
      </v-row>

      <v-row v-if="isMember && !user.localOrganization">
        <v-col cols="12" md="6">
          <v-checkbox v-model="wantsToJoinLocalOrganization" label="Присъединявам се към местна организация"
            @change="joinLocalOrganization" color="primary"></v-checkbox>
          <LocalOrganizationsSelect v-model="localOrganization"></LocalOrganizationsSelect>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-btn @click="prevStep" :disabled="loading" class="mr-5 mt-5 mb-5">Назад</v-btn>
          <v-btn @click="sendVote" :disabled="sendDisabled" color="primary" class="mt-5 mb-5">
            Изпрати <v-icon class="ml-3">send</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CryptoJS from 'crypto-js'
import JSEncrypt from 'jsencrypt'
import { KeyManager } from '@/KeyManager'
import LocalOrganizationsSelect from '@/components/Users/LocalOrganizationsSelect.vue'

export default {
  name: 'Candidates',
  props: ['value', 'maxChoices'],
  components: { LocalOrganizationsSelect },

  data() {
    return {
      step: 1,
      candidates: [],
      minSelections: 1,
      places: Array(this.value.length).fill().map((x, i) => {
        return { value: i + 1, text: i + 1, disabled: false }
      }),
      keyManager: null,
      err: [],
      votingEnabled: false,
      voted: false,
      alreadyVoted: false,
      ageDeclared: false,
      wantsToJoinLocalOrganization: false,
      localOrganization: false
    }
  },

  mounted() {
    if (this.election.registration_required) {
      this.checkRegistration()
    } else {
      this.initVoting()
    }

    // re-fetch user to check if member of local organization
    this.$store.commit('setLoading', true)
    this.$store.dispatch('getUser', this.user.ID).then(() => {
      this.$store.commit('setLoading', false)
    })
  },

  computed: {
    ...mapGetters(['user', 'isMember', 'dabgPublicKey', 'loading', 'election']),
    selected() {
      return this.candidates.filter(c => parseInt(c.place) > 0).sort((a, b) => a.place < b.place ? -1 : 1)
    },

    sendDisabled() {
      return this.loading || !this.ageDeclared ||
        (this.isMember && this.user.localOrganization === 0 && (!this.wantsToJoinLocalOrganization || !this.localOrganization))
    },

    choiceCount() {
      return this.candidates.filter(c => c.place > 0).length
    }
  },

  watch: {
    localOrganization: 'joinLocalOrganization',
    alreadyVoted() {
      this.$store.commit(
        'showAlert',
        {
          type: 'warning',
          message: 'Вече имате регистриран вот. Ако изпратите бюлетината отново, предният Ви вот ще отпадне.',
          dismissible: false
        }
      )
    }
  },

  methods: {
    candidateDisabled(candidate) {
      return (candidate.place === 0 || typeof candidate.place === 'undefined') &&
        this.choiceCount >= this.maxChoices
    },
    joinLocalOrganization() {
      if (this.wantsToJoinLocalOrganization && this.localOrganization) {
        this.$store.commit('setLoading', true)
        this.$store.dispatch('updateUser', {
          ...this.user,
          localOrganization: this.localOrganization
        }).finally(() => {
          this.$store.commit('setLoading', false)
        })
      }
    },

    checkRegistration() {
      this.keyManager = new KeyManager()

      this.$store.dispatch('checkOwnRegistration')
        .catch(err => {
          this.err.push(err.response.data.error)
        })
        // get DaBG public key
        .then(this.$store.dispatch('getPublicKey'))
        .catch(err => {
          console.error(err.error)
          this.$store.commit('showAlert', { type: 'error', message: 'Публичният ключ на eVote не е наличен в момента.' })
          this.votingEnabled = false
        })
        // initialize KeyManager
        .then(() => this.keyManager.init())
        // try to find user uploaded public key
        .then(() => this.$store.dispatch('getOwnPublicKey'))
        // compare uploaded key with locally stored
        .then(uploadedKey => {
          this.compareWithLocalKey(uploadedKey)
            // continue with voting
            .then(this.initVoting)
            .catch(message => {
              this.$store.commit('showAlert', { type: 'error', message })
              this.votingEnabled = false
            })
        })
        .catch(() => {
          // no uploaded key found, generate key pair
          this.keyManager.generateKeyPair(this.user.ID)
            // get key pair
            .then(() => this.keyManager.getKeyPair(this.user.ID))
            // upload public key
            .then(({ publicKey }) => this.$store.dispatch('uploadOwnPublicKey', publicKey))
            // continue with voting
            .then(this.initVoting)
            .catch(err => {
              this.votingEnabled = false
              this.$store.commit('showAlert', { type: 'error', message: err.message })
            })
        })
    },
    initVoting() {
      // check if user voted previously
      this.$store.dispatch('getOwnVote', this.election.id)
        .then(() => (this.alreadyVoted = true))
        .catch(() => (this.alreadyVoted = false))
        .finally(() => (this.votingEnabled = true))

      // set up candidates
      this.candidates = this.value.map((candidate, i) => {
        return { id: i + 1, name: candidate.choice_name, place: 0 }
      })
    },

    compareWithLocalKey(uploadedKey) {
      return new Promise((resolve, reject) => {
        this.keyManager.getKeyPair(this.user.ID)
          .then(storedKey => {
            if (typeof storedKey === 'undefined' || uploadedKey.data.key !== storedKey.publicKey) {
              reject(new Error(
                'Ключът за гласуване на Вашето устройство не отговаря на ключа, с който вече сте регистрирани за гласуван.'
              ))
            } else {
              resolve([uploadedKey, storedKey])
            }
          })
          .catch(() => {
            reject(new Error('Вече имате регистриран ключ за гласуване от друго устройство, който липсва на текущото.'))
          })
      })
    },

    nextStep() {
      this.step = 2
      this.collapsePlaces()
      window.scrollTo(0, 0)
    },
    prevStep() {
      this.step = 1
      window.scrollTo(0, 0)
    },

    makeSelection() {
      this.clearPlaces()

      // set taken places
      this.candidates.forEach(candidate => {
        if (parseInt(candidate.place) > 0) {
          this.places[candidate.place - 1].disabled = candidate.place > 0
        }
      })
    },

    // clear taken places
    clearPlaces() {
      this.places = this.places.map(place => {
        place.disabled = false
        return place
      })
    },

    collapsePlaces() {
      this.clearPlaces()

      this.selected.map((candidate, i) => {
        candidate.place = i + 1
        this.places[i].taken = true
      })
    },

    sendVote() {
      this.$store.commit('setLoading', true)

      // build vote object
      let vote = JSON.stringify({
        vote: this.selected.map(c => c.id),
        nonce: CryptoJS.lib.WordArray.random(16).toString()
      })

      if (this.election.registration_required) {
        // get user key pair
        this.keyManager.getKeyPair(this.user.ID).then(userKeyPair => {
          // encrypt vote
          const crypt = new JSEncrypt()
          crypt.setPublicKey(this.dabgPublicKey)
          const encryptedVote = crypt.encrypt(vote)

          // sign encrypted vote
          const sign = new JSEncrypt()
          sign.setPublicKey(userKeyPair.publicKey)
          sign.setPrivateKey(userKeyPair.privateKey)
          const signature = sign.sign(encryptedVote, CryptoJS.SHA256, 'sha256')
          const verified = sign.verify(encryptedVote, signature, CryptoJS.SHA256)

          if (verified) {
            this.$store.dispatch('vote', { election: this.election.id, vote: encryptedVote, signature }).then(() => {
              this.voted = true
              this.$store.commit('setLoading', false)
              this.$store.commit('showAlert', { type: 'success', message: 'Вашият вот беше успешно приет.', dismissible: false })
              window.scrollTo(0, 0)

            }).catch(() => {
              this.$store.commit('setLoading', false)
              this.$store.commit('showError', 'Възникна грешка при гласуването. Моля опитайте отново по-късно.')
            })
          }
        })
      } else {
        this.$store.dispatch('vote', { election: this.election.id, vote }).then(() => {
          this.voted = true
          this.$store.commit('setLoading', false)
          this.$store.commit('showAlert', { type: 'success', message: 'Вашият вот беше успешно приет.', dismissible: false })
          window.scrollTo(0, 0)
        }).catch(() => {
          this.$store.commit('setLoading', false)
          this.$store.commit('showError', 'Възникна грешка при гласуването. Моля опитайте отново по-късно.')
        })
      }
    }
  }
}
</script>

<style lang="scss">
.v-select.v-input--is-disabled {
  opacity: .5;
}
</style>
